import { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSearch, SearchResult, SearchResultRecipe, SearchResultCoupon, SearchResultLocation, SearchResultAd } from '@/hooks/use-search';
import { trackSearch, trackSearchResultClick } from '@/lib/analytics';
import { IconMapPin, IconNews, IconSearch, IconTag, IconToolsKitchen2 } from '@tabler/icons-react';

interface SearchDropdownProps {
  placeholder?: string;
  className?: string;
}

export const SearchDropdown = ({ placeholder = 'Search...', className }: SearchDropdownProps) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const { query, setQuery, results, clearSearch } = useSearch();
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Show dropdown when there are results
  useEffect(() => {
    if (debouncedQuery.length >= 2 && results.length > 0) {
      setOpen(true);
    } else if (debouncedQuery.length < 2) {
      setOpen(false);
    }
  }, [debouncedQuery, results]);

  // Track search queries
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      trackSearch({
        searchTerm: debouncedQuery,
        resultsCount: results.length,
      });
    }
  }, [debouncedQuery, results.length]);

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'ad':
        return IconNews;
      case 'location':
        return IconMapPin;
      case 'coupon':
        return IconTag;
      case 'recipe':
        return IconToolsKitchen2;
    }
  };

  const ResultIcon = ({ type }: { type: SearchResult['type'] }) => {
    const Icon = getIcon(type);
    return <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />;
  };

  const handleSelect = useCallback((result: SearchResult, position: number) => {
    // Track search result click
    trackSearchResultClick({
      searchTerm: debouncedQuery,
      resultType: result.type,
      resultId: result.type === 'recipe'
        ? String((result as SearchResultRecipe).data.id)
        : result.type === 'coupon'
        ? (result as SearchResultCoupon).data.id
        : result.type === 'location'
        ? String((result as SearchResultLocation).data.id)
        : (result as SearchResultAd).data.ad.AdTitle,
      resultTitle: result.title,
      position,
    });

    clearSearch();
    setOpen(false);

    switch (result.type) {
      case 'ad': {
        const adResult = result as SearchResultAd;
        // Navigate to ads page with the ad title to open the viewer
        navigate(`/ads?open=${encodeURIComponent(adResult.data.ad.AdTitle)}`);
        break;
      }
      case 'location': {
        const locationResult = result as SearchResultLocation;
        // Navigate to locations page with the location title as a URL param to open modal
        navigate(`/locations?open=${encodeURIComponent(locationResult.data.LocationTitle)}`);
        break;
      }
      case 'coupon': {
        const couponResult = result as SearchResultCoupon;
        // Navigate to coupons page with the coupon id to open the detail dialog
        navigate(`/coupons?open=${encodeURIComponent(couponResult.data.id)}`);
        break;
      }
      case 'recipe': {
        const recipeResult = result as SearchResultRecipe;
        // Navigate to recipes page with the recipe id to open the detail sheet
        navigate(`/recipes?open=${recipeResult.data.id}`);
        break;
      }
    }
  }, [navigate, clearSearch, debouncedQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.length < 2) {
      setOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Group results by type
  const locationResults = results.filter((r) => r.type === 'location');
  const adResults = results.filter((r) => r.type === 'ad');
  const recipeResults = results.filter((r) => r.type === 'recipe');
  const couponResults = results.filter((r) => r.type === 'coupon');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={`relative w-full ${className || ''}`}>
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="search"
            placeholder={placeholder}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (debouncedQuery.length >= 2 && results.length > 0) {
                setOpen(true);
              }
            }}
            className="pl-10 bg-[#f8f8f8] border-border"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={() => setOpen(false)}
      >
        <Command>
          <CommandList>
            {results.length === 0 && debouncedQuery.length >= 2 && (
              <CommandEmpty>No results found.</CommandEmpty>
            )}
            {locationResults.length > 0 && (
              <CommandGroup heading="Locations">
                {locationResults.map((result, idx) => (
                  <CommandItem
                    key={`location-${idx}`}
                    onSelect={() => handleSelect(result, idx + 1)}
                    className="cursor-pointer data-[selected=true]:bg-gray-100 data-[selected=true]:text-foreground"
                  >
                    <ResultIcon type={result.type} />
                    <div className="flex flex-col">
                      <span className="font-medium">{result.title}</span>
                      <span className="text-xs text-muted-foreground">{result.subtitle}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {adResults.length > 0 && (
              <CommandGroup heading="Ads">
                {adResults.map((result, idx) => (
                  <CommandItem
                    key={`ad-${idx}`}
                    onSelect={() => handleSelect(result, locationResults.length + idx + 1)}
                    className="cursor-pointer data-[selected=true]:bg-gray-100 data-[selected=true]:text-foreground"
                  >
                    <ResultIcon type={result.type} />
                    <div className="flex flex-col">
                      <span className="font-medium">{result.title}</span>
                      <span className="text-xs text-muted-foreground">{result.subtitle}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {couponResults.length > 0 && (
              <CommandGroup heading="Coupons">
                {couponResults.map((result, idx) => (
                  <CommandItem
                    key={`coupon-${idx}`}
                    onSelect={() => handleSelect(result, locationResults.length + adResults.length + idx + 1)}
                    className="cursor-pointer data-[selected=true]:bg-gray-100 data-[selected=true]:text-foreground"
                  >
                    <ResultIcon type={result.type} />
                    <div className="flex flex-col">
                      <span className="font-medium">{result.title}</span>
                      <span className="text-xs text-muted-foreground line-clamp-1">{result.subtitle}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {recipeResults.length > 0 && (
              <CommandGroup heading="Recipes">
                {recipeResults.map((result, idx) => (
                  <CommandItem
                    key={`recipe-${idx}`}
                    onSelect={() => handleSelect(result, locationResults.length + adResults.length + couponResults.length + idx + 1)}
                    className="cursor-pointer data-[selected=true]:bg-gray-100 data-[selected=true]:text-foreground"
                  >
                    <ResultIcon type={result.type} />
                    <div className="flex flex-col">
                      <span className="font-medium">{result.title}</span>
                      <span className="text-xs text-muted-foreground line-clamp-1">{result.subtitle}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};